<template>
  <div class="screen home">
    <p class="counter-band">
      A fresh themed 15×15 every day, a bigger 21×21 on Sundays, and a 101×101 Giant every season. Solve it on
      your own, race your friends' times, or share a code and fill it in together.
    </p>

    <section v-if="index === null" class="card"><p class="muted">Loading the puzzles&hellip;</p></section>

    <section v-else-if="!today" class="card card--today">
      <p class="card__eyebrow">{{ todayDate }}</p>
      <p class="muted">Today's puzzle is still being written. Check back in a little while — or pick one from the archive.</p>
    </section>

    <section v-else class="card card--today" @click="open(todayDate)">
      <p class="card__eyebrow">Today · {{ today.weekday }} · {{ today.difficulty }}</p>
      <h3>{{ today.title }}</h3>
      <p class="card__status">{{ status(todayDate) }}</p>
      <div class="card__actions">
        <button class="primary">{{ buttonLabel(todayDate) }}</button>
        <MyRoomButtons :date="todayDate" />
      </div>
    </section>

    <GroupCard v-for="id in groupIds" :key="id" :group-id="id" :date="todayDate" />

    <GiantCard v-for="g in giantsShown" :id="g.id" :key="g.id" :row="g" />

    <form class="join" @submit.prevent="join">
      <input v-model="code" placeholder="Friend's or group's code" maxlength="6" autocapitalize="characters" autocomplete="off" spellcheck="false">
      <button type="submit" class="secondary" :disabled="!code.trim()">Join</button>
    </form>

    <form v-if="naming" class="join join--name" @submit.prevent="startGroup">
      <input ref="groupName" v-model="groupName" placeholder="Group name, like Family" maxlength="30" autocomplete="off">
      <button type="submit" class="secondary" :disabled="!groupName.trim() || starting">Start</button>
    </form>
    <button v-else class="start-group" @click="beginNaming">Start a group: one code for every day's puzzle, together</button>
    <p v-if="roomError" class="notice notice--bad">{{ roomError }}</p>

    <ReminderBell />

    <section v-if="archive.length" class="archive">
      <h3 class="archive__title">Earlier puzzles</h3>
      <ul>
        <li v-for="row in archive" :key="row.date" @click="open(row.date)">
          <span class="archive__date">{{ shortDate(row.date) }}</span>
          <span class="archive__name">{{ row.title }}<small class="muted">{{ row.difficulty }}</small></span>
          <span class="archive__mark" :class="markClass(row.date)">{{ mark(row.date) }}</span>
        </li>
      </ul>
    </section>

    <MyStatsPanel />
    <LeaderBoard />

    <a class="hub-link" href="https://aroundtableround.com/" title="All the games">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.4" opacity="0.5" />
        <circle cx="20" cy="12" r="1.7" fill="currentColor" /><circle cx="17.66" cy="17.66" r="1.7" fill="currentColor" />
        <circle cx="12" cy="20" r="1.7" fill="currentColor" /><circle cx="6.34" cy="17.66" r="1.7" fill="currentColor" />
        <circle cx="4" cy="12" r="1.7" fill="currentColor" /><circle cx="6.34" cy="6.34" r="1.7" fill="currentColor" />
        <circle cx="12" cy="4" r="1.7" fill="currentColor" /><circle cx="17.66" cy="6.34" r="1.7" fill="currentColor" />
      </svg>
      Around Table Round
    </a>

    <p class="credit muted">Clues by Claude. Fill from Spread the Word(list).</p>

    <InstallPrompt />
  </div>
</template>

<script>
// The front door: today's puzzle (and any room I share for it), my standing groups (GroupCard), a code
// box for a friend's room or a group, starting a group, the
// daily reminder switch, the archive, and the house panels (stats, high scores, the way back to the
// hub, the install offer - which lives HERE and nowhere else).
import { mapState } from 'vuex';
import InstallPrompt from '@/components/shared/InstallPrompt.vue';
import MyStatsPanel from '@/components/shared/MyStatsPanel.vue';
import LeaderBoard from '@/components/shared/LeaderBoard.vue';
import ReminderBell from '@/components/home/ReminderBell.vue';
import GroupCard from '@/components/home/GroupCard.vue';
import GiantCard from '@/components/home/GiantCard.vue';
import MyRoomButtons from '@/components/home/MyRoomButtons.vue';
import { formatTime } from '@/engine/solving';
import { localDate } from '@/utils/dates';

export default {
  name: 'HomeScreen',
  components: {
    InstallPrompt, MyStatsPanel, LeaderBoard, ReminderBell, GroupCard, GiantCard, MyRoomButtons,
  },
  data() {
    return {
      code: '', todayDate: localDate(), naming: false, groupName: '', starting: false,
    };
  },
  computed: {
    ...mapState(['index', 'giants', 'progressByDate', 'roomError', 'groupIds']),
    // Every Giant whose season has begun, newest first (the next one is
    // made ahead and stays hidden until then). Weeks of solving outlast a
    // season, so the older ones stay too.
    giantsShown() {
      return Object.entries(this.giants || {})
        .map(([id, row]) => ({ id, ...row }))
        .filter((g) => g.startsOn && g.startsOn <= this.todayDate)
        .sort((a, b) => (a.startsOn < b.startsOn ? 1 : -1));
    },
    today() { return this.index?.[this.todayDate] || null; },
    archive() {
      return Object.entries(this.index || {})
        .filter(([date]) => date < this.todayDate)
        .sort(([a], [b]) => (a < b ? 1 : -1))
        .map(([date, row]) => ({ date, ...row }));
    },
  },
  methods: {
    open(date) { this.$store.dispatch('openPuzzle', date); },
    join() { this.$store.dispatch('joinRoomByCode', this.code); },
    beginNaming() {
      this.naming = true;
      this.$nextTick(() => this.$refs.groupName?.focus());
    },
    async startGroup() {
      this.starting = true;
      const made = await this.$store.dispatch('startGroup', this.groupName);
      this.starting = false;
      if (made) { this.naming = false; this.groupName = ''; }
    },
    progress(date) { return this.progressByDate[date] || null; },
    status(date) {
      const p = this.progress(date);
      if (!p || !p.filled) return 'Not started';
      if (p.solved) return `Solved in ${formatTime(p.seconds)}`;
      return `In progress · ${formatTime(p.seconds)}`;
    },
    buttonLabel(date) {
      const p = this.progress(date);
      if (p?.solved) return 'Look again';
      return p?.filled ? 'Resume' : 'Play';
    },
    mark(date) {
      const p = this.progress(date);
      if (p?.solved) return formatTime(p.seconds);
      return p?.filled ? '…' : '';
    },
    markClass(date) { return { 'is-solved': this.progress(date)?.solved }; },
    shortDate(date) {
      const d = new Date(`${date}T12:00:00`);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
  },
};
</script>

<style lang="scss" scoped>
// The header already says Crossword; the list starts right under it.
.home { justify-content: flex-start; gap: 0.8rem; padding-top: 0.8rem; }

.card {
  border: 1px solid var(--edge); border-radius: var(--radius); background: var(--surface); padding: 0.9rem 1rem;
  h3 { margin: 0.1rem 0 0.2rem; font-size: 1.3rem; }
}
.card__actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.6rem;
  .primary, :deep(.secondary) { flex: 1 1 8rem; width: auto; } }
.card--today { cursor: pointer; border-color: var(--accent-edge); }
.card__eyebrow { margin: 0; font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); font-weight: 800; }
.card__status { margin: 0; color: var(--ink-dim); font-size: 0.85rem; }

.join { display: flex; gap: 0.5rem;
  input { flex: 1; min-width: 0; text-transform: uppercase; letter-spacing: 0.15em; }
  .secondary { flex: 0 0 auto; width: auto; padding: 0 1rem; } }

.join--name input { text-transform: none; letter-spacing: normal; }
.start-group { background: none; border: 0; padding: 0; color: var(--ink-dim); font-size: 0.8rem; text-decoration: underline; cursor: pointer; text-align: left; }

.archive__title { margin: 0 0 0.3rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-dim); }
.archive ul { list-style: none; margin: 0; padding: 0; }
.archive li { display: flex; align-items: baseline; gap: 0.6rem; padding: 0.45rem 0.2rem; border-top: 1px solid var(--edge); cursor: pointer; font-size: 0.9rem; }
.archive__date { flex: 0 0 3.4rem; color: var(--ink-dim); font-size: 0.8rem; }
.archive__name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; small { font-size: 0.75rem; margin-left: 0.4rem; } }
.archive__mark { font-variant-numeric: tabular-nums; font-size: 0.8rem; color: var(--ink-dim); &.is-solved { color: var(--accent); font-weight: 700; } }
.credit { font-size: 0.72rem; text-align: center; margin: 0; }
</style>
